<script setup>
import { computed } from "vue";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";

/**
 * Primary brand CTA button.
 *
 * Hover treatment: the button lifts with a soft shadow while a circle wipes out
 * from the center (echoing the redesign's circular shapes). The fill colour and
 * the recoloured label live in an overlay revealed by a growing clip-path, so
 * the text changes colour exactly as the fill reaches it — never flashing a
 * clashing colour against the half-wiped background. On press it settles back.
 *
 * Renders an <a> when `href` is set, otherwise a <button>. Pass `as="span"` for
 * a purely visual CTA inside an element that is already a link (a whole-tile
 * anchor), so no interactive element nests inside another. Pass an `icon` (svg
 * path) to render a leading glyph masked with `currentColor`. For a bespoke inline
 * icon, use the `#icon` slot instead; it renders in both layers, sitting at
 * `iconRestClass` at rest and flipping to the wipe colour as the circle passes
 * (the slotted svg should use `currentColor`). Size, padding and positioning come
 * from the caller via `class`.
 *
 * Every inner layer is `pointer-events-none` so the root element is always the
 * click target. Analytics (GTM "Click Classes") reads the event target's classes,
 * so a class like `gtm-item-buy` passed from the caller only matches when the
 * click lands on the root, not on the label span inside it (GH-1486).
 */
const props = defineProps({
    href: {
        type: String,
        default: null,
    },
    // Force a presentational tag ("span") when the button sits inside a link.
    as: {
        type: String,
        default: null,
    },
    // Native button type when rendering a <button> (ignored with `href`).
    // Pass "submit" for form CTAs so Enter-to-submit keeps working.
    type: {
        type: String,
        default: "button",
    },
    variant: {
        type: String,
        default: "navy", // "navy" | "navyOnRed" | "white" | "whiteOnRed" | "whiteOnOrange"
    },
    icon: {
        type: String,
        default: null,
    },
    // Extra classes for the masked `icon` glyph (e.g. "max-md:hidden" to drop
    // the icon on narrow layouts where the label alone fits the mock).
    iconClass: {
        type: String,
        default: null,
    },
    // Tailwind colour class for a slotted #icon at rest (e.g. "text-brand-red").
    // Defaults to the label colour. The icon always flips to the wipe colour.
    iconRestClass: {
        type: String,
        default: null,
    },
    // Working state (GH-934): appends the BrandThrobber dots after the label,
    // blocks interaction, and suppresses the hover lift/wipe. The button keeps
    // full opacity — dimming stays reserved for genuinely disabled states.
    loading: {
        type: Boolean,
        default: false,
    },
});

// `base` = resting fill + label colour. `wipeBg`/`wipeText` = the colour the
// circle wipes in and the label colour revealed over it. Pairings keep the label
// legible on the incoming fill: white on navy, navy on white, white on red.
// Fills are `!important` so they win over the brand reset that zeroes the native
// <button> background (BrandButton renders a <button> when there's no href).
const variants = {
    navy: {
        base: "!bg-brand-navy text-white hover:shadow-[0_10px_22px_-6px_rgba(25,30,62,0.55)]",
        wipeBg: "bg-brand-red",
        wipeText: "text-white",
    },
    navyOnRed: {
        base: "!bg-brand-navy text-white hover:shadow-[0_12px_26px_-8px_rgba(0,0,0,0.45)]",
        wipeBg: "bg-white",
        wipeText: "text-brand-navy",
    },
    white: {
        base: "!bg-white text-brand-navy hover:shadow-[0_12px_26px_-8px_rgba(0,0,0,0.45)]",
        wipeBg: "bg-brand-red",
        wipeText: "text-white",
    },
    whiteOnRed: {
        base: "!bg-white text-brand-navy hover:shadow-[0_12px_26px_-8px_rgba(0,0,0,0.45)]",
        wipeBg: "bg-brand-navy",
        wipeText: "text-white",
    },
    whiteOnOrange: {
        base: "!bg-[#eb3700] text-white hover:shadow-[0_10px_22px_-6px_rgba(235,55,0,0.5)]",
        wipeBg: "bg-white",
        wipeText: "text-brand-red",
    },
};

const maskStyle = computed(() =>
    props.icon
        ? {
              maskImage: `url(${props.icon})`,
              WebkitMaskImage: `url(${props.icon})`,
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskPosition: "center",
              maskSize: "contain",
              WebkitMaskSize: "contain",
          }
        : null,
);
</script>

<template>
    <component
        :is="as ?? (href ? 'a' : 'button')"
        :href="as ? null : href"
        :type="as || href ? null : type"
        class="relative isolate inline-flex transform-gpu items-center justify-center overflow-hidden rounded-brand text-center font-brand font-bold transition-all duration-200"
        :class="[
            variants[variant].base,
            loading
                ? 'pointer-events-none'
                : 'group hover:-translate-y-0.5 active:translate-y-0 active:shadow-none',
        ]"
        :aria-busy="loading || undefined"
    >
        <!-- Invisible sizer: the only in-flow layer, so it sets the button's
             intrinsic size. Both visible layers below are absolutely positioned
             and identical, so the resting/wipe icon copies land on the same
             sub-pixel position and can't drift on hover. -->
        <span
            aria-hidden="true"
            class="pointer-events-none invisible inline-flex items-center justify-center gap-2"
        >
            <span
                v-if="icon"
                class="size-4 shrink-0"
                :class="iconClass"
                :style="maskStyle"
            ></span>
            <span v-if="$slots.icon" class="inline-flex shrink-0">
                <slot name="icon" />
            </span>
            <slot />
            <BrandThrobber v-if="loading" class="invisible" />
        </span>

        <!-- Resting label, sitting beneath the wipe. -->
        <span
            class="pointer-events-none absolute inset-0 z-10 flex transform-gpu items-center justify-center gap-2"
        >
            <span
                v-if="icon"
                aria-hidden="true"
                class="size-4 shrink-0 bg-current"
                :class="iconClass"
                :style="maskStyle"
            ></span>
            <span
                v-if="$slots.icon"
                class="inline-flex shrink-0"
                :class="iconRestClass"
            >
                <slot name="icon" />
            </span>
            <slot />
            <BrandThrobber v-if="loading" />
        </span>

        <!-- Wipe overlay: the incoming fill + recoloured label, revealed by a
             circle growing from the center. transform-gpu keeps the clipped edge
             crisp inside the rounded corners. -->
        <span
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 z-20 flex transform-gpu items-center justify-center gap-2 [clip-path:circle(0%_at_50%_50%)] [-webkit-clip-path:circle(0%_at_50%_50%)] transition-[clip-path] duration-300 ease-out group-hover:[clip-path:circle(80%_at_50%_50%)] group-hover:[-webkit-clip-path:circle(80%_at_50%_50%)]"
            :class="[variants[variant].wipeBg, variants[variant].wipeText]"
        >
            <span
                v-if="icon"
                class="size-4 shrink-0 bg-current"
                :class="iconClass"
                :style="maskStyle"
            ></span>
            <span v-if="$slots.icon" class="inline-flex shrink-0">
                <slot name="icon" />
            </span>
            <slot />
        </span>
    </component>
</template>
